/**
 * Signup experiment helpers (signup v1 vs v2).
 *
 * - `?signup=v2` / `?signup=v1` forces a variant and persists it in localStorage.
 * - Without the param, the stored variant is used; a new browser gets a random 50/50 split
 *   (`RANDOM_SPLIT_ENABLED`), sticky per browser. With the split off, the default is `v1` (control).
 * - `?demo=1` runs signup v2 without any write call or analytics.
 * - `?fail=<stage>` (demo only) forces an error state to review it.
 */

export const SIGNUP_VARIANT_STORAGE_KEY = 'ecometri-signup-variant';
export const SIGNUP_VARIANTS = ['v1', 'v2'];
export const RANDOM_SPLIT_ENABLED = true;
export const RANDOM_SPLIT_V2_PERCENT = 50;
export const DEMO_FAILURES = [
    'signup',
    'email',
    'storename',
    'phone',
    'login',
    'product',
    'photo',
    'store',
];

const getParams = (search) => new URLSearchParams(search || '');

// App runs logOut() (localStorage.clear) on every load without a session, before any page
// mounts; this module is evaluated earlier, so keep the assignment from boot.
let bootVariant = null;
try {
    bootVariant = window.localStorage.getItem(SIGNUP_VARIANT_STORAGE_KEY);
} catch {
    bootVariant = null;
}

// logOut() may also wipe the assignment after this visit stored it: write it back on unload.
// Intended: the A/B assignment is sticky per browser, survives a real logout too, and the last
// value seen by this tab wins (only a forced `?signup=` changes it).
try {
    window.addEventListener('pagehide', () => {
        if (!bootVariant) return;
        try {
            window.localStorage.setItem(SIGNUP_VARIANT_STORAGE_KEY, bootVariant);
        } catch {
            /* ignore */
        }
    });
} catch {
    /* no window */
}

// Backup copy of the assignment in a first-party cookie (1 year): survives localStorage.clear().
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const readCookieAssignment = () => {
    try {
        const match = document.cookie
            .split(';')
            .map((part) => part.trim())
            .find((part) => part.startsWith(`${SIGNUP_VARIANT_STORAGE_KEY}=`));
        return match ? decodeURIComponent(match.split('=')[1]) : null;
    } catch {
        return null;
    }
};

const writeCookieAssignment = (value) => {
    try {
        document.cookie = `${SIGNUP_VARIANT_STORAGE_KEY}=${encodeURIComponent(
            value
        )}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
    } catch {
        /* cookies unavailable */
    }
};

// Stored as `variant|source` (e.g. `v2|random`); older values are just `v2`.
const parseAssignment = (raw) => {
    const [variant, source] = String(raw || '').split('|');
    if (!SIGNUP_VARIANTS.includes(variant)) return null;
    return { variant, source: source || null };
};

const readStoredAssignment = () => {
    try {
        let stored = window.localStorage.getItem(SIGNUP_VARIANT_STORAGE_KEY);
        if (!stored) {
            stored = bootVariant || readCookieAssignment();
            if (stored) window.localStorage.setItem(SIGNUP_VARIANT_STORAGE_KEY, stored);
        }
        return parseAssignment(stored);
    } catch {
        return parseAssignment(bootVariant || readCookieAssignment());
    }
};

const storeAssignment = (variant, source) => {
    bootVariant = `${variant}|${source}`;
    writeCookieAssignment(bootVariant);
    try {
        window.localStorage.setItem(SIGNUP_VARIANT_STORAGE_KEY, bootVariant);
    } catch {
        /* storage unavailable: the param still decides this visit */
    }
};

/**
 * Resolves the signup variant for the current visit and how it was assigned.
 * `source` is the original assignment method and is kept across visits; `isNew` is true only
 * when this call created or changed the assignment (used to log the exposure once).
 * @param {string} search location.search
 * @returns {{
 *     variant: 'v1' | 'v2',
 *     source: 'query' | 'random' | 'default',
 *     isNew: boolean,
 * }}
 */
export const resolveSignupVariant = (search) => {
    const forced = getParams(search).get('signup');
    const stored = readStoredAssignment();

    if (SIGNUP_VARIANTS.includes(forced)) {
        // v2 navigation keeps `?signup=v2` in the URL: same variant is not a new assignment.
        if (stored && stored.variant === forced) {
            return { variant: forced, source: stored.source || 'query', isNew: false };
        }
        storeAssignment(forced, 'query');
        return { variant: forced, source: 'query', isNew: true };
    }

    if (stored) return { variant: stored.variant, source: stored.source || 'query', isNew: false };

    if (RANDOM_SPLIT_ENABLED) {
        const assigned = Math.random() * 100 < RANDOM_SPLIT_V2_PERCENT ? 'v2' : 'v1';
        storeAssignment(assigned, 'random');
        return { variant: assigned, source: 'random', isNew: true };
    }

    return { variant: 'v1', source: 'default', isNew: false };
};

/**
 * How the stored assignment for `variant` was made (`random` split or forced `query` link), sent
 * with the signup so the API can tell forced links apart in the A/B conversion.
 * @param {'v1' | 'v2'} variant
 * @returns {'random' | 'query' | null}
 */
export const getSignupVariantSource = (variant) => {
    const stored = readStoredAssignment();
    return stored && stored.variant === variant ? stored.source || 'query' : null;
};

/**
 * Resolves the signup variant for the current visit.
 * @param {string} search location.search
 * @returns {'v1' | 'v2'}
 */
export const getSignupVariant = (search) => resolveSignupVariant(search).variant;

/** @returns {boolean} true when `?demo=1` */
export const isSignupDemo = (search) => getParams(search).get('demo') === '1';

/**
 * Forced failure for demo/QA. Only meaningful in demo mode.
 * @returns {'signup'|'email'|'storename'|'phone'|'login'|'product'|'photo'|'store'|null}
 */
export const getDemoFailure = (search) => {
    const fail = getParams(search).get('fail');
    return DEMO_FAILURES.includes(fail) ? fail : null;
};
